import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useForegroundRefresh } from '../../hooks/useForegroundRefresh';
import crmApi, { ChatMessage, ContextMetrics, ToolOperationSummary } from '../../services/crmApi';
import AgentChatShell, { AgentChatMessage } from '../chat/AgentChatShell';

type Props = {
  assistantName?: string;
  focusContactId?: string;
  focusContactName?: string;
  onDataChanged?: () => void;
};

const CONTEXT_TOKEN_LIMIT = 200000;

const summarizeToolCalls = (toolCalls: NonNullable<ChatMessage['toolCalls']>): ToolOperationSummary[] => {
  return toolCalls.map((toolCall) => ({
    type: !toolCall.result.success
      ? 'error'
      : toolCall.name.startsWith('create_') || toolCall.name.startsWith('add_')
        ? 'created'
        : toolCall.name.startsWith('update_')
          ? 'updated'
          : toolCall.name.startsWith('delete_')
            ? 'deleted'
            : 'read',
    entity: toolCall.name,
    description: !toolCall.result.success
      ? `${toolCall.name}: ${toolCall.result.error || 'Unknown error'}`
      : toolCall.name.replace(/_/g, ' '),
  }));
};

export const CrmChat: React.FC<Props> = ({
  assistantName = 'CRM Agent',
  focusContactId,
  focusContactName,
  onDataChanged,
}) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const inline = isDesktop;
  const [draft, setDraft] = useState('');
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [contextMetrics, setContextMetrics] = useState<ContextMetrics | null>(null);
  const [recentToolCalls, setRecentToolCalls] = useState<Map<string, ToolOperationSummary[]>>(new Map());
  const refreshRequestIdRef = useRef(0);

  const refreshHistory = useCallback(async (opts?: { silent?: boolean }) => {
    const requestId = refreshRequestIdRef.current + 1;
    refreshRequestIdRef.current = requestId;

    try {
      if (!opts?.silent) setIsLoading(true);
      const { messages: serverMessages, contextMetrics: serverMetrics } = await crmApi.getChatHistory();

      if (refreshRequestIdRef.current !== requestId) {
        return;
      }

      setMessages(serverMessages);
      setContextMetrics(serverMetrics);
      setError((previous) => previous === 'Failed to send message. Please try again.' ? null : previous);
    } catch (err) {
      console.error('Failed to load CRM chat history:', err);
    } finally {
      if (!opts?.silent && refreshRequestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  useForegroundRefresh(() => refreshHistory({ silent: true }), {
    minHiddenMs: 0,
    staleAfterMs: 0,
  });

  const canSend = useMemo(() => draft.trim().length > 0 && !isAssistantTyping, [draft, isAssistantTyping]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || isAssistantTyping) return;

    setDraft('');
    setError(null);
    setIsAssistantTyping(true);

    const optimisticUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      sender: 'user',
      content: text,
      contextTokens: null,
      createdAt: new Date().toISOString(),
      threadId: '',
      hasToolCalls: false,
    };

    setMessages((previous) => [...previous, optimisticUserMessage]);

    try {
      const { message, userMessage, contextMetrics: nextContextMetrics, toolCallSummary } =
        await crmApi.sendMessage(text, focusContactId);

      setMessages((previous) => {
        const withoutOptimistic = previous.filter((messageItem) => !messageItem.id.startsWith('temp-'));
        return [...withoutOptimistic, userMessage, message];
      });
      setContextMetrics(nextContextMetrics);

      if (toolCallSummary && toolCallSummary.length > 0) {
        setRecentToolCalls((previous) => {
          const next = new Map(previous);
          next.set(message.id, toolCallSummary);
          return next;
        });

        if (toolCallSummary.some((operation) => operation.type !== 'read' && operation.type !== 'error')) {
          onDataChanged?.();
        }
      }
    } catch (err) {
      console.error('Failed to send CRM chat message:', err);
      if (document.visibilityState === 'hidden') {
        return;
      }
      setMessages((previous) => previous.filter((messageItem) => !messageItem.id.startsWith('temp-')));
      setDraft(text);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsAssistantTyping(false);
    }
  }, [draft, focusContactId, isAssistantTyping, onDataChanged]);

  const contextUsagePercent = useMemo(() => {
    if (!contextMetrics) return 0;
    return Math.min(100, (contextMetrics.totalTokens / CONTEXT_TOKEN_LIMIT) * 100);
  }, [contextMetrics]);

  const contextUsageColor = useMemo(() => {
    if (contextUsagePercent < 50) return 'var(--color-success, #22c55e)';
    if (contextUsagePercent < 80) return 'var(--color-warning, #eab308)';
    return 'var(--color-error, #ef4444)';
  }, [contextUsagePercent]);

  const subtitle = focusContactName ? `Focused on ${focusContactName}` : 'CRM chat';

  const shellMessages = useMemo<AgentChatMessage[]>(() => {
    return messages.map((message) => ({
      id: message.id,
      sender: message.sender === 'user' ? 'user' : 'assistant',
      content: message.content,
      toolOperations: message.hasToolCalls && message.toolCalls
        ? summarizeToolCalls(message.toolCalls)
        : recentToolCalls.get(message.id),
    }));
  }, [messages, recentToolCalls]);

  return (
    <AgentChatShell
      inline={inline}
      assistantName={assistantName}
      subtitle={subtitle}
      contextIndicator={contextMetrics ? {
        title: `Context: ${contextMetrics.totalTokens.toLocaleString()} tokens (${contextUsagePercent.toFixed(1)}%)`,
        percent: contextUsagePercent,
        color: contextUsageColor,
      } : undefined}
      error={error}
      onDismissError={() => setError(null)}
      isLoading={isLoading}
      messages={shellMessages}
      isAssistantTyping={isAssistantTyping}
      typingLabel="CRM agent is typing"
      emptyStateTitle="Start a conversation"
      emptyStateText="Ask about a contact, have the agent log new CRM context, or tell it to update a record."
      draft={draft}
      onDraftChange={setDraft}
      placeholder={focusContactName ? `Ask about ${focusContactName}` : 'Message'}
      inputAriaLabel="CRM message"
      canSend={canSend}
      onSend={send}
      sendAriaLabel="Send CRM message"
      footerHint="Enter to send • Shift+Enter for newline"
      launcherAriaLabel="Open CRM chat"
      closeAriaLabel="Close CRM chat"
      dialogAriaLabel="CRM chat"
      onClose={() => {
        setError(null);
        setIsAssistantTyping(false);
      }}
    />
  );
};

export default CrmChat;
